import React, { useEffect, useState, useRef } from 'react';

interface TaskEvent {
  status: string;
  stage: string;
  progress?: {
    current: number;
    total: number;
    percent: number;
    message: string;
  };
}

export const BrowserTaskPopup: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0 });

  useEffect(() => {
    let source: EventSource | null = null;
    try {
      const key = localStorage.getItem('omnigate_api_key') || '';
      const sseUrl = key ? `/v1/browser-agent/events?token=${encodeURIComponent(key)}` : '/v1/browser-agent/events';
      source = new EventSource(sseUrl);  // proxied through backend — no CORS
      source.onmessage = (e) => {
        const data = JSON.parse(e.data);
        setTasks((prev) => {
          const idx = prev.findIndex((t) => t.task_id === data.task_id);
          if (idx >= 0) {
            const newTasks = [...prev];
            newTasks[idx] = data;
            return newTasks;
          }
          return [...prev, data];
        });
      };
    } catch (e) {
      console.error("SSE Error", e);
    }
    return () => {
      source?.close();
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    dragRef.current.startX = e.clientX - pos.x;
    dragRef.current.startY = e.clientY - pos.y;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setPos({
      x: e.clientX - dragRef.current.startX,
      y: e.clientY - dragRef.current.startY,
    });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    setIsDragging(false);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  };

  if (tasks.filter(t => t.status === 'running' || t.status === 'pending').length === 0) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 w-96 bg-[#1a1c23] border border-blue-500/30 rounded-lg shadow-2xl z-50 overflow-hidden font-mono text-xs transition-transform duration-0"
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)`, touchAction: 'none' }}
    >
      <div 
        className="bg-[#242730] px-3 py-2 border-b border-blue-500/30 flex justify-between items-center text-blue-400 cursor-move hover:bg-[#2a2e38]"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <span className="font-bold flex items-center pointer-events-none">
          <i className="fas fa-globe mr-2 animate-pulse"></i>
          MCP Browser Tasks
        </span>
        <button 
          onClick={onClose} 
          className="hover:text-blue-300 pointer-events-auto z-10 p-1"
          onPointerDown={(e) => e.stopPropagation()} // Prevent closing from dragging
        >
          <i className="fas fa-times"></i>
        </button>
      </div>
      <div className="p-3 max-h-64 overflow-y-auto custom-scrollbar">
        {tasks.filter(t => t.status === 'running' || t.status === 'pending').map((t, idx) => (
          <div key={idx} className="mb-3 last:mb-0">
            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
              <span>{t.tool} ({t.task_id})</span>
              <span className="uppercase text-blue-400">{t.stage || t.status}</span>
            </div>
            {t.progress && (
              <div className="w-full bg-[#0d0f14] rounded-full h-1.5 mb-1 mt-2">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${t.progress.percent || 0}%` }}
                ></div>
              </div>
            )}
            <div className="text-slate-300 text-[10px] truncate">
              {t.progress?.message || "Running task..."}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
